<template>
  <TheNavigationHeader
    class="bg-white border-b border-solid border-mercury shadow-light max-w-full"
  >
    <div slot="leftSide">
      <div
        class="block"
        :class="{
          hidden: expandInput && mobileSearch,
          block: !expandInput && mobileSearch
        }"
      >
        <div @click.stop="toggleOrgSelector">
          <MenuHeader
            class="px-2 ml-2 cursor-pointer"
            :class="{
              'opacity-25 cursor-not-allowed': !isMultiLocation,
              'py-1 rounded hover:bg-iron': isMultiLocation,
              hidden: expandInput && mobileSearch,
              block: !expandInput && mobileSearch,
              'pointer-events-none': isLoadingFranchisors
            }"
            ref="user-menu-header"
          >
            <template #title>
              <LoadingBlurredText
                class="w-20 h-4 mb-1"
                v-if="isLoadingFranchisors"
              />
              <div v-else>{{ locationMenuTitle }}</div>
            </template>
            <template #subtitle>
              <LoadingBlurredText
                class="w-32 h-3"
                v-if="isLoadingFranchisors"
              />
              <div v-else>{{ locationMenuSubtitle }}</div>
            </template>
            <template #icon>
              <div>
                <BaseIcon
                  :name="orgSelectorToggle ? 'chevron-up' : 'chevron-down'"
                  width="14"
                />
              </div>
            </template>
          </MenuHeader>
        </div>
        <BaseTransition>
          <OrganizationSelector
            class="absolute"
            @close="closeOrgSelector"
            v-click-outside="{
              exclude: ['user-menu-header'],
              handler: closeOrgSelector
            }"
            v-if="orgSelectorToggle && isMultiLocation"
          />
        </BaseTransition>
      </div>
    </div>
    <div class="flex items-center flex-row-reverse w-auto" slot="rightSide">
      <TheUserMenu
        :user="user"
        @item-clicked="menuClicked"
        @logout="logout"
        :class="{
          hidden: expandInput && mobileSearch,
          block: !expandInput && mobileSearch
        }"
      />
      <BaseLink
        ref="support-btn"
        :href="'support.listen360.com/'"
        :class="{
          hidden: expandInput && mobileSearch,
          block: !expandInput && mobileSearch
        }"
        class="text-bombay border-2 border-white border-solid bg-white hover:text-abbey mr-4 rounded-full"
      >
        <BaseIcon name="solid/question-circle" width="26" />
      </BaseLink>
      <div
        class="pr-1 bg-white"
        :class="{
          'w-full': expandInput && mobileSearch,
          absolute: expandInput && mobileSearch,
          'left-0': expandInput && mobileSearch,
          relative: !expandInput && mobileSearch,
          'left-auto': !expandInput && mobileSearch
        }"
      >
        <Search
          ref="nav-search"
          @search="onSearch"
          @show_mobile_search="setNavMobileSearch"
          @show_desk_search="setNavDeskSearch"
          @expand_input="updateExpand"
        />
      </div>
    </div>
  </TheNavigationHeader>
</template>

<script>
import {
  LoadingBlurredText,
  MenuHeader,
  Search,
  TheNavigationHeader,
  TheUserMenu,
  clickOutside
} from '@listen360/alexandria'
import get from 'lodash/get'
import { mapActions, mapGetters } from 'vuex'

import OrganizationSelector from '@/components/OrganizationSelector'

export default {
  name: 'TheNavigation',
  directives: {
    clickOutside
  },
  components: {
    LoadingBlurredText,
    OrganizationSelector,
    MenuHeader,
    Search,
    TheNavigationHeader,
    TheUserMenu
  },
  data() {
    return {
      isLoadingFranchisors: false,
      orgSelectorToggle: false
    }
  },
  mounted() {
    this.isLoadingFranchisors = true

    this.getAccessibleFranchisors()
      .then(() => {
        const franchisorId = get(this.$router, 'currentRoute.params.orgId')

        if (!franchisorId) {
          return Promise.resolve()
        }

        return this.getAccessibleFranchisor({
          franchisorId
        }).then(franchisor => {
          this.setCurrentEnvironment(franchisor)

          const subsidiary = franchisor.subsidiaries.find(
            sub => sub.id === franchisorId
          ) || { id: franchisorId }

          this.setCurrentSubsidiary(subsidiary)
        })
      })
      .finally(() => {
        this.isLoadingFranchisors = false
      })
  },
  computed: {
    ...mapGetters({
      isMultiLocation: 'user/isMultiLocation',
      currentEnvironment: 'user/currentEnvironment',
      currentSubsidiary: 'user/currentSubsidiary',
      user: 'user/current',
      expandInput: 'navigation/expandInput',
      searchTerm: 'navigation/searchTerm',
      mobileSearch: 'navigation/mobileSearch',
      deskSearch: 'navigation/deskSearch'
    }),
    locationMenuTitle() {
      return this.currentEnvironment.name || 'Listen360'
    },
    locationMenuSubtitle() {
      return this.currentSubsidiary.name || 'Executive Summary'
    }
  },
  methods: {
    ...mapActions({
      getAccessibleFranchisor: 'user/getAccessibleFranchisor',
      getAccessibleFranchisors: 'user/getAccessibleFranchisors',
      logout: 'user/logout',
      resetTempEnvironment: 'user/resetTempEnvironment',
      setCurrentEnvironment: 'user/setCurrentEnvironment',
      setCurrentSubsidiary: 'user/setCurrentSubsidiary',
      setDeskSearch: 'navigation/setDeskSearch',
      setExpandInput: 'navigation/setExpandInput',
      setMobileSearch: 'navigation/setMobileSearch',
      setSearchTerm: 'navigation/setSearchTerm'
    }),
    closeOrgSelector() {
      this.orgSelectorToggle = false
      this.resetTempEnvironment()
    },
    menuClicked(link) {
      if (link.path) {
        this.$router.push(link.path)
      }
    },
    onSearch(query) {
      this.setSearchTerm(query)
      this.$router.push({
        name: 'search',
        query: {
          q: query
        }
      })
    },
    toggleOrgSelector() {
      if (this.isMultiLocation) {
        this.orgSelectorToggle = !this.orgSelectorToggle
        this.resetTempEnvironment()
      }
    },
    updateExpand(val) {
      this.setExpandInput(val)
    },
    setNavDeskSearch(val) {
      this.setMobileSearch(!val)
      this.setDeskSearch(val)
    },
    setNavMobileSearch(val) {
      this.setMobileSearch(val)
      this.setDeskSearch(!val)
    }
  }
}
</script>
